import React, { useState } from 'react'
import moment from 'moment'
import {
  Box,
  ButtonBase,
  Collapse,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { viz } from '../../../userPages/dashboard/managerOverview/overviewTheme'
import StaffRowComponent from './StaffRowComponent'

const SEVERITY = {
  critical: { color: viz.critical, icon: ErrorOutlineIcon, label: 'Critical' },
  warning: { color: viz.warning, icon: WarningAmberIcon, label: 'Attention' },
}

const plural = (count, singular, pluralWord) =>
  `${count} ${count === 1 ? singular : (pluralWord ?? `${singular}s`)}`

const byFirstName = (a, b) =>
  (a.firstName ?? '').localeCompare(b.firstName ?? '')

const mapUserAccount = (userAccount, text) => ({
  id: userAccount?._id,
  firstName: userAccount?.firstName,
  lastName: userAccount?.lastName,
  photograph: userAccount?.photograph,
  text,
})

// Normalises the raw list responses into an ordered worklist — only
// categories with people appear, most severe first. Categories whose fetch
// failed arrive as null and are skipped rather than shown as zero.
export const buildActionItems = (lists) => {
  if (!lists) return []
  const {
    expiredTraining,
    dbs,
    licences,
    licenceChecks,
    idCards,
    trainingReviews,
    appraisals,
    occupationalHealth,
  } = lists

  const items = []

  if (expiredTraining) {
    items.push({
      key: 'expired-training',
      severity: 'critical',
      label: (c) => `${plural(c, 'staff member')} with expired training`,
      people: expiredTraining
        .map((userAccount) => mapUserAccount(userAccount, 'Training expired'))
        .sort(byFirstName),
      url: '/employees/mandatory-training',
      urlLabel: 'Open the training matrix',
    })
  }

  if (dbs) {
    items.push({
      key: 'dbs',
      severity: 'critical',
      label: (c) => `${plural(c, 'staff member')} without an in-date DBS`,
      people: [
        ...(dbs.staffWithoutDBS ?? []).map((employee) =>
          mapUserAccount(employee.userAccount, 'No DBS recorded'),
        ),
        ...(dbs.expiredDBS ?? []).map((employee) =>
          mapUserAccount(
            employee.userAccount,
            employee.latestDBS?.completed
              ? `Certificate dated ${moment(employee.latestDBS.completed).format(
                  'D MMM YYYY',
                )}`
              : 'No completed DBS certificate',
          ),
        ),
      ].sort(byFirstName),
      url: '/dbs-checks',
      urlLabel: 'Open DBS checks',
    })
  }

  if (licences) {
    items.push({
      key: 'expired-licences',
      severity: 'critical',
      label: (c) => `${plural(c, 'expired driving licence')}`,
      people: licences
        .map((employee) =>
          mapUserAccount(
            employee.userAccount,
            employee.personnelInfo?.driving?.licenceExpiry
              ? `Expired ${moment(
                  employee.personnelInfo.driving.licenceExpiry,
                ).format('D MMM YYYY')}`
              : 'Licence expired',
          ),
        )
        .sort(byFirstName),
    })
  }

  if (licenceChecks) {
    items.push({
      key: 'licence-checks',
      severity: 'warning',
      label: (c) => `${plural(c, 'driving licence check')} overdue`,
      people: licenceChecks
        .map((employee) =>
          mapUserAccount(employee.userAccount, 'Check due'),
        )
        .sort(byFirstName),
    })
  }

  if (idCards) {
    items.push({
      key: 'id-cards',
      severity: 'warning',
      label: (c) => `${plural(c, 'staff member')} without an ID card`,
      people: idCards
        .map((employee) => mapUserAccount(employee.userAccount, 'No ID card'))
        .sort(byFirstName),
    })
  }

  if (trainingReviews) {
    const seen = new Set()
    const people = []
    for (const record of trainingReviews) {
      const userAccount = record.employee?.userAccount
      if (!userAccount?._id || seen.has(userAccount._id)) continue
      seen.add(userAccount._id)
      people.push(mapUserAccount(userAccount, 'Certificate awaiting review'))
    }
    items.push({
      key: 'training-reviews',
      severity: 'warning',
      label: (c) => `${plural(c, 'training upload')} to review`,
      people: people.sort(byFirstName),
      url: '/pending-mandatory-training',
      urlLabel: 'Review uploads',
    })
  }

  if (appraisals) {
    const waitingForStaff = appraisals.filter(
      (appraisal) => appraisal.status === 'Awaiting Employee',
    ).length
    items.push({
      key: 'appraisals',
      severity: 'warning',
      label: (c) =>
        `${plural(c, 'open appraisal')}${
          waitingForStaff > 0 ? ` (${waitingForStaff} waiting for staff)` : ''
        }`,
      people: appraisals
        .map((appraisal) =>
          mapUserAccount(
            appraisal.employee?.userAccount,
            appraisal.status === 'Awaiting Employee'
              ? 'Waiting for staff'
              : 'Awaiting HR review',
          ),
        )
        .sort(byFirstName),
      url: '/appraisals/pending-review',
      urlLabel: 'Open appraisals',
    })
  }

  if (occupationalHealth) {
    items.push({
      key: 'occupational-health',
      severity: 'warning',
      label: (c) =>
        `${plural(c, 'staff member')} with outstanding occupational health screening`,
      people: occupationalHealth
        .map((employee) => mapUserAccount(employee.userAccount, employee.reason))
        .sort(byFirstName),
      url: '/occupational-health?filter=outstanding',
      urlLabel: 'Open occupational health',
    })
  }

  return items
    .map((item) => ({ ...item, count: item.people.length }))
    .filter((item) => item.count > 0)
    .sort((a, b) =>
      a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1,
    )
}

const ActionRow = ({ item, index, expanded, onToggle }) => {
  const config = SEVERITY[item.severity]
  const Icon = config.icon

  return (
    <Stack
      component={motion.div}
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.3 }}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `3px solid ${config.color}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{
          p: 1.25,
          gap: 1.25,
          justifyContent: 'flex-start',
          textAlign: 'left',
          '&:hover': { backgroundColor: 'anMuted.main' },
        }}
      >
        <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
        <Box flexGrow={1}>
          <Typography
            variant="body2"
            sx={{ color: 'text.primary', fontSize: '0.85rem' }}
          >
            {item.label(item.count)}
          </Typography>
          <Typography variant="smallGrey" sx={{ fontSize: '0.7rem' }}>
            {config.label}
          </Typography>
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'text.disabled',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>
      <Collapse in={expanded}>
        <Stack px={1} pb={1} gap={0.25}>
          <Stack sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {item.people.map((person) => (
              <StaffRowComponent
                key={person.id ?? `${person.firstName}-${person.lastName}`}
                firstName={person.firstName}
                lastName={person.lastName}
                photograph={person.photograph}
                text={person.text}
                id={person.id}
              />
            ))}
          </Stack>
          {item.url && (
            <Stack
              component={Link}
              to={item.url}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={0.5}
              sx={{
                textDecoration: 'none',
                color: 'contrast.main',
                px: 1,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <Typography variant="caption" fontWeight={600} color="inherit">
                {item.urlLabel}
              </Typography>
              <ArrowForwardIosIcon sx={{ fontSize: 11 }} />
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Stack>
  )
}

// The workforce worklist — every person blocking compliance, grouped by
// issue and expandable to the individuals so each name is one click away
const ActionCentre = ({ items, loading }) => {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())

  const toggle = (key) =>
    setExpandedKeys((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack>
        <Typography variant="title">Needs your attention</Typography>
        <Typography variant="smallGrey">
          Workforce compliance, prioritised
        </Typography>
      </Stack>
      {loading ? (
        <Stack mt={1.5} gap={1}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={54} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Stack alignItems="center" py={5} gap={1}>
          <CheckCircleOutlineIcon sx={{ fontSize: 42, color: viz.good }} />
          <Typography variant="body2" textAlign="center">
            All clear — every staff member is compliant and nothing is waiting
            on you.
          </Typography>
        </Stack>
      ) : (
        <Stack mt={1.5} gap={1}>
          {items.map((item, index) => (
            <ActionRow
              key={item.key}
              item={item}
              index={index}
              expanded={expandedKeys.has(item.key)}
              onToggle={() => toggle(item.key)}
            />
          ))}
        </Stack>
      )}
    </Paper>
  )
}

export default ActionCentre
